/**
 * A custom element that only loads and plays the product card hover video while it is meant to be seen.
 *
 * Sources are kept in `data-src` until the first time the card is hovered, so a grid of cards costs
 * nothing until the customer actually interacts with one. Playback pauses (and rewinds) as soon as the
 * video is hidden again, and never starts while the card is outside of the viewport.
 *
 * Alternate cards (`.card-gallery[data-metafield-default]`) show the hover media by default and hide it
 * on hover, so their playback condition is inverted.
 */
class CardHoverVideo extends HTMLElement {
  /** @type {boolean} */
  #hovered = false;
  /** @type {boolean} */
  #visible = false;
  /** @type {boolean} */
  #loaded = false;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {Element | null} */
  #card = null;

  connectedCallback() {
    this.#card = this.closest('product-card') ?? this.closest('.card-gallery');

    this.#card?.addEventListener('pointerenter', this.#handlePointerEnter);
    this.#card?.addEventListener('pointerleave', this.#handlePointerLeave);

    this.#observer = new IntersectionObserver(this.#handleIntersect, { rootMargin: '200px' });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#card?.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.#card?.removeEventListener('pointerleave', this.#handlePointerLeave);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersect = (entries) => {
    const entry = entries[entries.length - 1];
    if (!entry) return;

    this.#visible = entry.isIntersecting;
    this.#update();
  };

  #handlePointerEnter = () => {
    this.#hovered = true;
    this.#update();
  };

  #handlePointerLeave = () => {
    this.#hovered = false;
    this.#update();
  };

  /**
   * Whether this card shows its hover media by default (alternate cards), which inverts the hover state.
   * The attribute is set by a script after render, so it is read lazily.
   * @returns {boolean}
   */
  get #shownByDefault() {
    return this.closest('.card-gallery')?.hasAttribute('data-metafield-default') ?? false;
  }

  /** @returns {HTMLVideoElement | null} */
  get #video() {
    return this.querySelector('video');
  }

  #update() {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const shouldPlay = this.#visible && (this.#shownByDefault ? !this.#hovered : this.#hovered);

    if (shouldPlay) {
      this.#load();
      this.#play();
    } else {
      this.#pause();
    }
  }

  #load() {
    if (this.#loaded) return;
    this.#loaded = true;

    const video = this.#video;
    if (!video) return;

    for (const source of this.querySelectorAll('source[data-src]')) {
      const { src } = /** @type {HTMLSourceElement} */ (source).dataset;
      if (src) source.setAttribute('src', src);
      source.removeAttribute('data-src');
    }

    video.load();
  }

  #play() {
    const video = this.#video;
    if (!video) return;

    // Safari needs the muted property set programmatically to allow inline playback.
    video.muted = true;
    video.play().catch(() => {
      // Autoplay can be refused (low power mode, browser policy); the poster stays visible.
    });
  }

  #pause() {
    const video = this.#video;
    if (!video || video.paused) return;

    video.pause();
    video.currentTime = 0;
  }
}

if (!customElements.get('card-hover-video')) {
  customElements.define('card-hover-video', CardHoverVideo);
}
